import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AccessTokenProvider, AlertProvider } from "context";
import { usePosthog } from "hooks/usePosthog";
import useQueryClient from "hooks/useQueryClient";
import { useUserIdentity } from "hooks/useUserIdentity";

import { ErrorBoundary, ErrorPage } from "pages/ErrorState";
import { QueryClientProvider } from "react-query";
import { RecoilRoot } from "recoil";

import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { BulkEquipmentUploadBanner } from "components/molecules/BulkEquipmentUploadBanner/BulkEquipmentUploadBanner";
import { PortfolioExportDownloadBanner } from "components/molecules/DownloadBanner/DownloadBanner";
import GlobalAlert from "components/molecules/GlobalAlert";
import UploadBanner from "components/molecules/UploadBanner/UploadBanner";
import { UploadProvider } from "context/UploadContext";
import ProtectedRoute from "hooks/useProtectedRoute";
import BuildingEnergyPage from "pages/BuildingPage/BuildingEnergyPage";
import MeasureReportProvider from "pages/BuildingPage/ProjectReport/MeasureReportProvider";
import PoliciesPage from "pages/CarbonPoliciesPage/CarbonPoliciesPage";
import PropertyVirtualAuditPage from "pages/PropertyPage/VirtualAudit/PropertyVirtualAuditPage";
import Reports from "pages/ReportsPage/ReportsPage";
import BuildingSandboxPage from "pages/SandboxPage/SandboxBuildingPage";
import BuildingSandboxLayout from "pages/SandboxPage/SandboxLayout";
import PortfolioSandboxPage from "pages/SandboxPage/SandboxPortfolioPage";
import SettingsPage from "pages/SettingsPage/SettingsPage";
import { UserPermissions } from "utils/enums";
import Header from "./components/organisms/Header";
import BuildingPage from "./pages/BuildingPage";
import HelpLayout from "./pages/HelpPage/HelpLayout";
import HelpPage from "./pages/HelpPage/HelpPage";
import PortfolioPage from "./pages/PortfolioPage";

function App() {
    const { accessToken, error, isAuthenticated } = useUserIdentity();
    const queryClient = useQueryClient();

    if (error) return <ErrorPage />;
    if (!isAuthenticated || !accessToken) return null;

    return (
        <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
                <AccessTokenProvider accessToken={accessToken}>
                    <AlertProvider>
                        <PosthogWrapper />
                        <RecoilRoot>
                            <UploadProvider>
                                <BrowserRouter>
                                    <Header />
                                    <GlobalAlert />
                                    <div className="viewport">
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <Routes>
                                                {/* Sandbox route */}
                                                <>
                                                    <Route
                                                        path="/sandbox"
                                                        element={
                                                            <ProtectedRoute
                                                                permissionRequired={UserPermissions.UseInternalTools}
                                                            >
                                                                <PortfolioSandboxPage />
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                    <Route
                                                        path="/sandbox/building/:buildingModelUid"
                                                        element={
                                                            <ProtectedRoute
                                                                permissionRequired={UserPermissions.UseInternalTools}
                                                            >
                                                                <BuildingSandboxLayout title="🧪 Building Sandbox">
                                                                    <BuildingSandboxPage />
                                                                </BuildingSandboxLayout>
                                                            </ProtectedRoute>
                                                        }
                                                    />
                                                </>
                                                {/* End Sandbox route */}
                                                <Route
                                                    path="/help/:helpTopic/:helpSubTopic"
                                                    element={
                                                        <ProtectedRoute permissionRequired={UserPermissions.InAppHelp}>
                                                            <HelpLayout title="Help">
                                                                <HelpPage />
                                                            </HelpLayout>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route path="*" element={<>404 Not Found</>} />
                                                <Route path="/" element={<Navigate to="/portfolio" />} />
                                                <Route path="/portfolio" element={<PortfolioPage />} />
                                                <Route path="/reports/*" element={<Reports />} />
                                                <Route path="/policies" element={<PoliciesPage />} />
                                                <Route
                                                    path="/settings"
                                                    element={
                                                        <ProtectedRoute permissionRequired={UserPermissions.ReadUser}>
                                                            <SettingsPage />
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/help/:helpTopic/:helpSubTopic?"
                                                    element={
                                                        <ProtectedRoute permissionRequired={UserPermissions.InAppHelp}>
                                                            <HelpLayout title="Help">
                                                                <HelpPage />
                                                            </HelpLayout>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                <Route path="/buildings/*" element={<BuildingPage />} />
                                                <Route
                                                    path="/properties/:propertyUid/virtual-audit"
                                                    element={<PropertyVirtualAuditPage />}
                                                />
                                                <Route
                                                    path="/building/:buildingModelUid/measures/:carbonReductionMeasureUid"
                                                    element={<MeasureReportProvider />}
                                                />
                                                <Route
                                                    path="/building/:buildingModelUid/energy"
                                                    element={<BuildingEnergyPage />}
                                                />
                                            </Routes>
                                        </Suspense>
                                    </div>
                                    <BulkEquipmentUploadBanner />
                                    <PortfolioExportDownloadBanner />
                                    <UploadBanner />
                                </BrowserRouter>
                            </UploadProvider>
                        </RecoilRoot>
                    </AlertProvider>
                </AccessTokenProvider>
            </ErrorBoundary>
        </QueryClientProvider>
    );
}

function PosthogWrapper() {
    usePosthog();
    return null;
}

export default withAuthenticationRequired(App);
